import React, { useMemo, useRef, useState } from 'react';
import { gsap, useGSAP, prefersReducedMotion } from '../../lib/gsap';
import { useReveal } from '../Fx/useReveal';

export interface LedgerEntry {
   name: string;
   worth?: string;
   meta?: string;
   description?: string;
   image?: string;
}

/** "$258.8 billion" → 258.8e9. Unparseable values return 0 so the bar simply doesn't render. */
const parseWorth = (value?: string) => {
   if (!value) return 0;
   const n = parseFloat(value.replace(/[^0-9.]/g, ''));
   if (Number.isNaN(n)) return 0;
   if (/trillion/i.test(value)) return n * 1e12;
   if (/billion/i.test(value)) return n * 1e9;
   if (/million/i.test(value)) return n * 1e6;
   return n;
};

const formatTotal = (n: number) =>
   n >= 1e12 ? `$${(n / 1e12).toFixed(2)} trillion` : n >= 1e9 ? `$${(n / 1e9).toFixed(1)} billion` : `$${n}`;

const GRID = 'md:grid-cols-[4rem_5rem_1fr_auto_2rem]';

const Row = ({
   entry,
   rank,
   share,
   open,
   onToggle,
}: {
   entry: LedgerEntry;
   rank: number;
   share: number;
   open: boolean;
   onToggle: () => void;
}) => {
   const row = useRef<HTMLLIElement>(null);
   const body = useRef<HTMLDivElement>(null);
   const bar = useRef<HTMLSpanElement>(null);

   useGSAP(
      () => {
         if (!body.current) return;
         gsap.to(body.current, {
            height: open ? 'auto' : 0,
            autoAlpha: open ? 1 : 0,
            duration: prefersReducedMotion() ? 0 : 0.8,
            ease: 'expo.inOut',
         });
      },
      { scope: row, dependencies: [open] }
   );

   useGSAP(
      () => {
         if (!bar.current || prefersReducedMotion()) return;
         gsap.from(bar.current, {
            scaleX: 0,
            duration: 1.8,
            ease: 'expo.out',
            scrollTrigger: { trigger: bar.current, start: 'top 95%', once: true },
         });
      },
      { scope: row }
   );

   return (
      <li ref={row} className="border-b border-white/[0.07]" data-reveal>
         <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            className={`group grid w-full grid-cols-[2.5rem_1fr_auto] items-center gap-4 py-6 text-left md:gap-8 ${GRID}`}
         >
            <span className="font-mono text-xs text-signal">{String(rank).padStart(2, '0')}</span>

            <span className="hidden h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-ink-800 ring-1 ring-inset ring-white/10 md:flex">
               {!entry.image && (
                  <span className="font-display text-2xl text-bone-dim transition-colors group-hover:text-signal">
                     {entry.name
                        .split(/\s+/)
                        .filter(w => /^[A-Z]/.test(w))
                        .slice(0, 2)
                        .map(w => w[0])
                        .join('')}
                  </span>
               )}
               {entry.image && (
                  <img
                     src={entry.image}
                     alt=""
                     loading="lazy"
                     className={`h-full w-full object-cover transition duration-700 ${
                        open ? 'grayscale-0' : 'grayscale group-hover:grayscale-0'
                     }`}
                  />
               )}
            </span>

            <span className="min-w-0">
               <span className="block font-display text-3xl leading-none text-bone transition-transform duration-500 group-hover:translate-x-1 md:text-5xl">
                  {entry.name}
               </span>
               {entry.meta && <span className="mt-2 block truncate text-sm text-bone-muted">{entry.meta}</span>}
               {share > 0 && (
                  <span className="mt-4 block h-px w-full max-w-md bg-white/[0.06]">
                     <span
                        ref={bar}
                        className="block h-px origin-left bg-signal"
                        style={{ width: `${Math.max(share * 100, 2)}%` }}
                     />
                  </span>
               )}
            </span>

            {entry.worth ? (
               <span className="text-right font-mono text-sm text-bone md:text-base">{entry.worth}</span>
            ) : (
               <span />
            )}

            <span
               className={`hidden h-8 w-8 items-center justify-center rounded-full border text-bone-muted transition-all duration-500 md:flex ${
                  open ? 'rotate-45 border-signal bg-signal text-ink-950' : 'border-white/15 group-hover:border-bone'
               }`}
               aria-hidden="true"
            >
               +
            </span>
         </button>

         <div ref={body} className="invisible h-0 overflow-hidden opacity-0">
            <div className={`grid grid-cols-1 gap-6 pb-10 md:gap-8 ${GRID}`}>
               <div className="hidden md:col-span-2 md:block" />
               {entry.image && (
                  <img src={entry.image} alt={entry.name} loading="lazy" className="w-40 rounded-sm md:hidden" />
               )}
               <p className="max-w-2xl text-base leading-relaxed text-bone-muted md:col-span-3 md:text-lg">
                  {entry.description}
               </p>
            </div>
         </div>
      </li>
   );
};

const Ledger = ({ entries, unit = 'Net worth' }: { entries: LedgerEntry[]; unit?: string }) => {
   const scope = useRef<HTMLDivElement>(null);
   const [openIndex, setOpenIndex] = useState<number | null>(0);
   const max = useMemo(() => Math.max(...entries.map(e => parseWorth(e.worth)), 1), [entries]);
   const total = useMemo(() => entries.reduce((sum, e) => sum + parseWorth(e.worth), 0), [entries]);

   useReveal(scope, [entries]);

   return (
      <div ref={scope} className="page">
         <div className="mb-14 grid grid-cols-2 gap-6 md:grid-cols-4" data-reveal>
            <div>
               <p className="eyebrow mb-2">Individuals</p>
               <p className="font-display text-5xl">{String(entries.length).padStart(2, '0')}</p>
            </div>
            {total > 0 && (
               <div className="md:col-span-2">
                  <p className="eyebrow mb-2">Combined {unit.toLowerCase()}</p>
                  <p className="font-display text-5xl text-signal">{formatTotal(total)}</p>
               </div>
            )}
         </div>

         <div className={`hidden gap-8 border-b border-white/[0.07] pb-3 md:grid ${GRID}`} data-reveal>
            <span className="eyebrow">#</span>
            <span />
            <span className="eyebrow">Name</span>
            <span className="eyebrow text-right">{unit}</span>
            <span />
         </div>
         <ul>
            {entries.map((entry, i) => (
               <Row
                  key={entry.name}
                  entry={entry}
                  rank={i + 1}
                  share={parseWorth(entry.worth) / max}
                  open={openIndex === i}
                  onToggle={() => setOpenIndex(openIndex === i ? null : i)}
               />
            ))}
         </ul>
      </div>
   );
};

export default Ledger;
